import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { uniqBy } from 'rambda';
import type { ApplicationDataType } from '@/components/contacts/application-list/application-list-item';
import {
  generateListResponseScheme,
  type ListRespondedWith,
  type RespondedType,
  type ResponseError,
} from '@/models/response';
import { type FriendApplication, friendApplicationScheme } from '@/models/user/friend-application';
import { addToast } from '@/stores/toast';
import { splitBy } from '@/utils/array';
import { fetcher } from '@/utils/fetcher';

export async function getFriendApplications(signal?: AbortSignal): Promise<ApplicationDataType[]> {
  const [pendingApplications, historyApplications] = await Promise.all([
    fetcher<ListRespondedWith<FriendApplication>>('/GetFriendApplications', undefined, {
      signal,
      schema: generateListResponseScheme(friendApplicationScheme),
      validResponseCodes: [0, 1],
    }),
    fetcher<ListRespondedWith<FriendApplication>>('/GetHistoryApplications', undefined, {
      signal,
      schema: generateListResponseScheme(friendApplicationScheme),
      validResponseCodes: [0, 1],
    }),
  ]);

  const applications = [...(pendingApplications.Data ?? []), ...(historyApplications.Data ?? [])];

  return uniqBy((d) => d.ID, applications).map((d) => ({
    id: d.ID,
    userId: d.UserID,
    userName: d.UserNickName,
    avatarUrl: d.UserHeadImg,
    createTime: d.CreateTime,
    status: d.Status === 0 ? 'pending' : d.Status === 1 ? 'accepted' : 'rejected',
    remark: d.Remark,
  }));
}

interface FriendApplications {
  applications: ApplicationDataType[];
  pastApplications: ApplicationDataType[];
}

export const friendApplicationListQueryOptions = queryOptions<ApplicationDataType[]>({
  queryKey: ['/GetFriendApplications'],
  queryFn: ({ signal }) => getFriendApplications(signal),
  placeholderData: [],
  staleTime: 15 * 1000,
});

export default function useFriendApplications() {
  return useQuery({
    ...friendApplicationListQueryOptions,
    select: (data): FriendApplications => {
      const [applications, pastApplications] = splitBy(data, (d) => d.status === 'pending');
      return {
        applications,
        pastApplications,
      };
    },
  });
}

export function useRespondFriendApplication(application: ApplicationDataType) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, 'accept' | 'reject'>({
    mutationKey: ['/RespondFriendApply', application.id],
    mutationFn: (params) =>
      fetcher(
        params === 'accept' ? '/AcceptFriendApply' : '/RejectFriendApply',
        { ID: application.id },
        { validResponseCodes: [0] },
      ),
    onSuccess: (_, params) => {
      queryClient.setQueryData(friendApplicationListQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (!draft) return;
          const item = draft.find((d) => d.id === application.id);
          if (item) item.status = params === 'accept' ? 'accepted' : 'rejected';
        }),
      );
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}
